import {
  ATOMIC_BOMB_SILHOUETTE,
  ATOMIC_BOMB_TEXTURE_HEIGHT,
  ATOMIC_BOMB_TEXTURE_WIDTH,
  type AtomicGraphics,
  type AtomicTextureScene,
  atomicHullShade,
  ensureAtomicCoreTexture,
  ensureAtomicEmberTexture,
  ensureAtomicFireballTexture,
  ensureAtomicSmokeTexture,
} from './atomic-bomb-art';

/**
 * **O Arsenal Ígneo: o míssil, e o fogo que ele deixa.**
 *
 * ## O tamanho não é uma escolha desta arte
 *
 * O contrato do gate V28G.L diz que o míssil ocupa **exatamente 70 % da Bomba
 * Atômica** - 70 % do comprimento visual e 70 % da largura máxima - e a maneira
 * mais segura de isso ser verdade não é medir duas coisas e comparar: é DESENHAR
 * DENTRO DA MESMA CAIXA e deixar uma escala uniforme fazer o resto.
 *
 * {@link MISSILE_SILHOUETTE} é a silhueta da Bomba, importada e não copiada.
 * O chamador escala por `(raio de corpo x 2) / MISSILE_BODY_SPAN`, exatamente
 * como escala a Bomba, e como o raio de corpo do míssil é 70 % do dela - derivado
 * nos dois `balance.ts` a partir de `MISSILE_SIZE_BPS` - as duas dimensões saem
 * a 70 % **por construção**, com um único fator nos dois eixos. A proporção não
 * pode mudar com o zoom porque não há dois fatores para divergirem.
 *
 * O que muda é o que está DENTRO da caixa: um corpo cilíndrico branco muito mais
 * fino do que o da bomba, um nariz vermelho, estabilizadores vermelhos que vão
 * até à borda da caixa, e uma chama.
 *
 * ## O volume é a mesma matemática da Bomba, avaliada uma vez
 *
 * {@link atomicHullShade} é importado. É `N . L` para um cilindro de eixo
 * horizontal, assado na textura ao gerá-la em vez de por quadro, e é o que faz um
 * retângulo branco ler-se como um tubo. Copiá-lo aqui seria manter duas rampas
 * que podem divergir; o que difere é a COR que ela ilumina - grafite lá, branco
 * aqui - e isso é um argumento, não uma segunda função.
 *
 * ## A chama é um SPRITE próprio, e a orientação é uma função só
 *
 * Assada na textura do corpo ela era um detalhe de vinte pixels em cento e
 * oitenta e quatro, e desaparecia no zoom mínimo. Fora dele pode ter a sua
 * escala e a sua mistura aditiva - fogo é luz, e um corpo branco não pode ser -
 * e continua presa à traseira REAL, porque o deslocamento local é rodado pela
 * MESMA {@link missileRotation} que orienta o corpo.
 *
 * Seis quadros para a sala inteira, escolhidos por uma função pura do tempo, e
 * um pool de sprites: nunca um emitter por míssil.
 *
 * ## O que este ficheiro nunca sabe
 *
 * Que joia existe, que nível ela tem, quanto dano faz, quem lançou e quem levou.
 * Ele recebe uma cena e um índice de quadro, e devolve uma chave de textura.
 */

/* -------------------------------------------------------------------------- */
/*  A caixa, herdada da Bomba                                                 */
/* -------------------------------------------------------------------------- */

/**
 * A silhueta do míssil: **a mesma caixa da Bomba**, importada.
 *
 * Não é uma cópia dos números dela e não é uma medida independente - é o mesmo
 * objeto. É isso que faz "70 % em cada eixo" ser uma consequência de uma escala
 * uniforme em vez de uma coincidência que alguém tem de verificar.
 */
export const MISSILE_SILHOUETTE = ATOMIC_BOMB_SILHOUETTE;

/**
 * O comprimento DESENHADO do míssil dentro da textura, em pixels.
 *
 * Igual ao da Bomba, e é o ponto: o chamador divide o comprimento de mundo que
 * quer por este número, e como o comprimento de mundo do míssil é 70 % do da
 * Bomba, a escala sai a 70 % da dela.
 */
export const MISSILE_BODY_SPAN = ATOMIC_BOMB_SILHOUETTE.span;

/** As dimensões da moldura, iguais às da Bomba pela mesma razão. */
export const MISSILE_TEXTURE_WIDTH = ATOMIC_BOMB_TEXTURE_WIDTH;
export const MISSILE_TEXTURE_HEIGHT = ATOMIC_BOMB_TEXTURE_HEIGHT;

/* -------------------------------------------------------------------------- */
/*  A geometria, em pixels da textura                                         */
/* -------------------------------------------------------------------------- */

/** O eixo do corpo, na vertical da moldura. */
const AXIS_Y = MISSILE_TEXTURE_HEIGHT / 2;

/**
 * **O NARIZ está no `+x` da textura, e essa é a correção do V28G.L.1.**
 *
 * ## A auditoria, e o que ela encontrou
 *
 * A primeira versão desenhava o bico em `x = 10` - o extremo **esquerdo** - com a
 * cauda e as aletas a crescerem para a direita. As duas cenas rodam o sprite por
 * `atan2(dirY, dirX)`, portanto um míssil a viajar para `+x` era desenhado sem
 * rotação nenhuma e apontava o bico para `-x`: **de ponta-cabeça**, exatamente
 * como a avaliação manual descreveu.
 *
 * ## Porque a correção é na ARTE e não na rotação
 *
 * Porque assim a textura passa a ser auto-descritiva: o `+x` local É o nariz, e a
 * transformação da cena é `atan2` e mais nada. A alternativa - somar pi na cena -
 * deixaria a arte a mentir e poria uma negação que a segunda cena pode esquecer.
 * {@link missileRotation} existe na mesma, partilhada pelas duas, para que nunca
 * haja duas respostas para a mesma pergunta.
 *
 * A caixa não muda: o vão continua a ser {@link MISSILE_BODY_SPAN} e a
 * meia-altura continua a ser a da Bomba, portanto os 70 % continuam a sair de uma
 * escala uniforme.
 */
const TIP_X = 174;
/** O outro extremo da caixa: a ponta das aletas, atrás. */
const SPAN_END_X = TIP_X - MISSILE_BODY_SPAN;

/**
 * **Meia-altura do corpo: 17 px contra os 27 da Bomba.**
 *
 * É o número que separa "míssil" de "bomba" dentro de uma caixa idêntica. A
 * Bomba é um cilindro gordo com uma razão de 3:1; este é um tubo de 4,8:1, que é
 * a proporção que um míssil tem. A caixa não muda - o que muda é quanto dela o
 * corpo enche, e o resto é ocupado pelas aletas.
 */
const BODY_HALF = 17;

/** A ogiva: onde ela acaba e encontra o cilindro. Curta, e vermelha. */
const NOSE_END_X = TIP_X - 34;
/** Onde o cilindro acaba e a secção da cauda começa. */
const TAIL_START_X = TIP_X - 118;
/** Onde a estrutura acaba e a tubeira começa. */
const TAIL_END_X = TIP_X - 132;

/**
 * Até onde as aletas sobem, em pixels a partir do eixo.
 *
 * Exatamente a meia-altura da CAIXA. As aletas são o que enche a caixa em altura,
 * e é deliberado: elas são a parte vermelha mais larga da silhueta, e são elas
 * que fazem "as pontas vermelhas permanecerem legíveis no zoom mínimo" - um
 * detalhe que ocupasse a altura do corpo desapareceria.
 */
const FIN_HALF = MISSILE_SILHOUETTE.halfHeight;

/** Onde as aletas começam e onde a ponta delas fecha a caixa, ATRÁS. */
const FIN_FROM_X = TIP_X - 104;
const FIN_TO_X = SPAN_END_X;

/** O branco do corpo, antes da luz. Um branco quebrado, nunca `0xffffff`. */
const HULL_WHITE = 0xf2f4f8;
/** O vermelho do nariz e das aletas. */
const NOSE_RED = 0xd7202a;
const FIN_RED = 0xc31e27;
/** O escuro da tubeira, para a chama ter de onde sair. */
const NOZZLE_DARK = 0x2a2c31;

/* -------------------------------------------------------------------------- */
/*  A chama                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Quantos quadros de chama existem.
 *
 * Oito e já não seis: com seis a cintilação lia-se como um pulso regular, que é
 * o que a avaliação manual chamou "sem graça". São oito texturas pequenas,
 * criadas uma vez e partilhadas por toda a cena - o custo é o mesmo de duas
 * imagens a mais, e não há emitter nenhum por míssil.
 */
export const MISSILE_FLAME_FRAMES = 8;

/** Duração de um ciclo completo da chama, em ms. Curto: é fogo, não um pulso. */
export const MISSILE_FLAME_PERIOD_MS = 180;

/**
 * O quadro de chama para um instante, como função PURA do tempo.
 *
 * Determinística e sem estado, portanto duas cenas com o mesmo relógio desenham
 * o mesmo quadro - e um teste pode verificá-la sem abrir um canvas. Nunca decide
 * mecânica: uma chama não toca em dano, colisão, alvo nem tempo de vida.
 */
export function missileFlameFrame(elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs)) {
    return 0;
  }
  const period = MISSILE_FLAME_PERIOD_MS;
  const phase = (((elapsedMs % period) + period) % period) / period;
  const index = Math.floor(phase * MISSILE_FLAME_FRAMES);
  return index >= MISSILE_FLAME_FRAMES ? MISSILE_FLAME_FRAMES - 1 : index;
}

/**
 * Os limites do comprimento da chama, em pixels da moldura DELA.
 *
 * Já não têm de caber na caixa do corpo: a chama é um sprite próprio, desenhado
 * na sua própria moldura e ancorado à tubeira. O que ela não pode é ser curta
 * demais para se ver no zoom mínimo, e foi por isso que cresceu.
 */
const MIN_FLAME_LENGTH = 34;
const MAX_FLAME_LENGTH = 60;

/** A moldura da chama, e onde ela nasce dentro dela. */
export const MISSILE_FLAME_TEXTURE_WIDTH = 88;
export const MISSILE_FLAME_TEXTURE_HEIGHT = 56;

/**
 * **O eixo da chama, medido na moldura DELA.**
 *
 * Esta linha é a correção do V28G.L.4, e vale a pena dizer o que estava errado:
 * a chama era desenhada em `AXIS_Y`, que é `MISSILE_TEXTURE_HEIGHT / 2` = **50**,
 * o eixo do CORPO - mas a moldura dela tinha quarenta pixels de altura, e
 * `generateTexture` recorta a partir de (0,0). A chama vivia entre os y 37 e 63
 * de uma imagem que acabava aos 40: o que chegava ao ecrã eram três pixels da
 * aba de baixo, um risco em vez de uma chama.
 *
 * Nenhuma prova o apanhou porque nenhuma comparava as coordenadas desenhadas
 * com a moldura em que iam ser recortadas. Agora há uma, e é por isso que este
 * eixo é uma constante própria em vez de um empréstimo: um número que quer dizer
 * "metade da minha moldura" não pode vir da moldura de outro.
 */
const FLAME_AXIS_Y = MISSILE_FLAME_TEXTURE_HEIGHT / 2;

/** A raiz encosta à direita da moldura: a chama cresce para `-x`, para trás. */
const FLAME_ROOT_X = 84;
/** Meia-altura da chama na raiz. Um pouco mais fina do que o corpo. */
const FLAME_HALF = 15;

/**
 * O comprimento da chama nesse quadro, em pixels.
 *
 * Uma onda triangular sobre o índice, para o ciclo fechar sem salto: o quadro
 * zero e o último são vizinhos no tempo, portanto têm de ser vizinhos no
 * tamanho.
 *
 * Já não guarda a caixa do corpo - a chama saiu para o seu próprio sprite, e os
 * 70 % vivem na textura do corpo, que ela não toca. O que a limita agora é a
 * MOLDURA dela, e isso é afirmado onde é decidido: uma prova mede a caixa de
 * todas as primitivas de todos os quadros contra
 * {@link MISSILE_FLAME_TEXTURE_WIDTH} e {@link MISSILE_FLAME_TEXTURE_HEIGHT}.
 */
export function missileFlameLength(frame: number): number {
  const t = ((frame % MISSILE_FLAME_FRAMES) + MISSILE_FLAME_FRAMES) % MISSILE_FLAME_FRAMES;
  const half = MISSILE_FLAME_FRAMES / 2;
  const triangle = t <= half ? t / half : 2 - t / half;
  return MIN_FLAME_LENGTH + triangle * (MAX_FLAME_LENGTH - MIN_FLAME_LENGTH);
}

/* -------------------------------------------------------------------------- */
/*  O desenho                                                                 */
/* -------------------------------------------------------------------------- */

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function mix(from: number, to: number, amount: number): number {
  const t = clamp01(amount);
  const fr = (from >> 16) & 0xff;
  const fg = (from >> 8) & 0xff;
  const fb = from & 0xff;
  const tr = (to >> 16) & 0xff;
  const tg = (to >> 8) & 0xff;
  const tb = to & 0xff;
  return (
    (Math.round(fr + (tr - fr) * t) << 16) |
    (Math.round(fg + (tg - fg) * t) << 8) |
    Math.round(fb + (tb - fb) * t)
  );
}

/**
 * A cor de um paralelo do cilindro, dada a rampa de luz da Bomba.
 *
 * Sobre um corpo BRANCO a rampa não pode multiplicar: `0xf2f4f8 x 3` satura em
 * branco puro e apaga o volume inteiro. Ela interpola entre uma sombra fria e um
 * realce quase branco, que é o que dá a um tubo claro a mesma leitura de volume
 * que a multiplicação dá a um corpo escuro.
 */
function hullTone(k: number): number {
  const shade = atomicHullShade(k);
  /* A rampa útil vive em ~[0,17, 1,9]; normalizada para `[0, 1]`. */
  const t = clamp01((shade - 0.17) / 1.5);
  const base = mix(0x4a5060, HULL_WHITE, t);
  return shade > 1.25 ? mix(base, 0xffffff, Math.min(0.7, (shade - 1.25) * 0.9)) : base;
}

/** A meia-altura do corpo em `x`: ogiva à frente, cilindro, aperto na cauda. */
function halfHeightAt(x: number): number {
  if (x > TIP_X || x < TAIL_END_X) {
    return 0;
  }
  if (x >= NOSE_END_X) {
    /* Uma ogiva cónica arredondada: cresce depressa e assenta no ombro. */
    const t = (TIP_X - x) / (TIP_X - NOSE_END_X);
    return BODY_HALF * Math.sqrt(Math.max(0, t));
  }
  if (x >= TAIL_START_X) {
    return BODY_HALF;
  }
  const t = (TAIL_START_X - x) / (TAIL_START_X - TAIL_END_X);
  return BODY_HALF * (1 - 0.28 * t);
}

/**
 * As aletas: dois triângulos vermelhos, um para cada lado, DESENHADOS PRIMEIRO.
 *
 * Primeiro para o corpo as tapar parcialmente e elas ficarem ATRÁS, que é o que
 * dá profundidade - a mesma ordem que a Bomba usa e pela mesma razão.
 *
 * Uma terceira aleta, mais curta e centrada no eixo, sugere a que aponta para a
 * câmera: sem ela um par de triângulos lê-se como uma seta plana.
 */
function drawFins(g: AtomicGraphics): void {
  for (const sign of [-1, 1]) {
    g.fillStyle(FIN_RED, 1);
    g.fillTriangle(
      FIN_FROM_X,
      AXIS_Y + sign * BODY_HALF * 0.7,
      FIN_TO_X,
      AXIS_Y + sign * FIN_HALF,
      FIN_TO_X + 18,
      AXIS_Y + sign * BODY_HALF * 0.55,
    );
    /* Uma aresta clara no bordo de ataque, para a aleta ter espessura. */
    g.fillStyle(mix(FIN_RED, 0xffffff, 0.35), 0.8);
    g.fillTriangle(
      FIN_FROM_X,
      AXIS_Y + sign * BODY_HALF * 0.7,
      FIN_FROM_X - 22,
      AXIS_Y + sign * FIN_HALF * 0.72,
      FIN_FROM_X - 4,
      AXIS_Y + sign * BODY_HALF * 0.68,
    );
  }
  /* A aleta central, escura e curta: profundidade sem competir com as outras. */
  g.fillStyle(mix(FIN_RED, 0x000000, 0.4), 1);
  g.fillTriangle(
    FIN_FROM_X - 6,
    AXIS_Y - BODY_HALF * 0.2,
    FIN_TO_X + 6,
    AXIS_Y - BODY_HALF * 0.1,
    FIN_TO_X + 6,
    AXIS_Y + BODY_HALF * 0.3,
  );
}

/** O corpo, uma linha de cada vez, com a rampa cilíndrica assada. */
function drawBody(g: AtomicGraphics): void {
  for (let dy = -BODY_HALF; dy <= BODY_HALF; dy += 1) {
    const k = dy / BODY_HALF;
    const colour = hullTone(k);
    let start = -1;
    let end = -1;
    for (let x = TAIL_END_X; x <= TIP_X; x += 1) {
      if (halfHeightAt(x) >= Math.abs(dy)) {
        if (start < 0) {
          start = x;
        }
        end = x;
      }
    }
    if (start < 0) {
      continue;
    }
    g.fillStyle(colour, 1);
    g.fillRect(start, AXIS_Y + dy, end - start + 1, 1);
  }
}

/**
 * **A OGIVA VERMELHA**, por cima do corpo, e a razão de ela ser redesenhada.
 *
 * O corpo já cobriu a ogiva em branco; esta passagem pinta-a de vermelho com a
 * MESMA rampa de luz, para que ela seja parte do mesmo cilindro em vez de um
 * decalque colado à frente. É o detalhe que faz a ponta ler-se como uma ponta.
 */
function drawNose(g: AtomicGraphics): void {
  for (let dy = -BODY_HALF; dy <= BODY_HALF; dy += 1) {
    const k = dy / BODY_HALF;
    const shade = atomicHullShade(k);
    const t = clamp01((shade - 0.17) / 1.5);
    const colour = mix(mix(0x3d0a0e, NOSE_RED, t), 0xffffff, shade > 1.3 ? 0.35 : 0);
    let start = -1;
    let end = -1;
    for (let x = NOSE_END_X; x <= TIP_X; x += 1) {
      if (halfHeightAt(x) >= Math.abs(dy)) {
        if (start < 0) {
          start = x;
        }
        end = x;
      }
    }
    if (start < 0) {
      continue;
    }
    g.fillStyle(colour, 1);
    g.fillRect(start, AXIS_Y + dy, end - start + 1, 1);
  }
  /* A junta entre a ogiva e o corpo: uma linha escura, e a escala lê-se. */
  g.fillStyle(0x000000, 0.35);
  g.fillRect(NOSE_END_X - 2, AXIS_Y - BODY_HALF, 2, BODY_HALF * 2);
  /*
   * E um anel vermelho estreito à frente da cauda.
   *
   * O contrato pede "detalhes suficientes para as pontas vermelhas permanecerem
   * legíveis no zoom mínimo", e no zoom mínimo a ogiva sozinha é uma mancha de
   * três pixels. Um segundo sinal vermelho na outra ponta faz o objeto ler-se
   * como "branco com extremidades vermelhas" mesmo quando nada mais se distingue.
   */
  g.fillStyle(NOSE_RED, 0.9);
  g.fillRect(TAIL_START_X + 4, AXIS_Y - BODY_HALF * 0.95, 6, BODY_HALF * 1.9);
}

/** A tubeira escura, de onde a chama sai. */
function drawNozzle(g: AtomicGraphics): void {
  const half = halfHeightAt(TAIL_END_X + 1);
  g.fillStyle(NOZZLE_DARK, 1);
  g.fillRect(TAIL_END_X, AXIS_Y - half, 6, half * 2);
  g.fillStyle(0x000000, 0.75);
  g.fillEllipse(TAIL_END_X - 1, AXIS_Y, half * 1.1, half * 1.7);
}

/**
 * **A chama, vermelho-alaranjada e sempre dentro da caixa.**
 *
 * Três camadas concêntricas - vermelho escuro, laranja, e um núcleo quase branco
 * - desenhadas como triângulos que apontam para trás. O comprimento vem de
 * {@link missileFlameLength}, cujo máximo é escolhido para a ponta nunca passar
 * a ponta das aletas: o vão desenhado é a caixa, e uma chama que a esticasse
 * mudaria a proporção que os 70 % dependem de ser fixa.
 */
/**
 * **A propulsão, num sprite PRÓPRIO e não assada no corpo** (gate V28G.L.1).
 *
 * ## Porque saiu da textura do míssil
 *
 * Porque assada lá dentro ela era um detalhe de vinte pixels numa imagem de
 * cento e oitenta e quatro, desenhada à mesma escala do corpo: no zoom mínimo
 * desaparecia, que é exatamente o que a avaliação manual reportou. Um sprite
 * próprio pode ter a sua escala, a sua mistura ADITIVA - fogo é luz, e o corpo
 * não pode ser - e a sua pulsação, sem que nada disso toque no corpo branco.
 *
 * ## As três camadas, de fora para dentro
 *
 * Vermelho na extremidade, laranja no corpo da chama, branco-amarelo no núcleo.
 * É a ordem em que uma chama real se lê, e é a que o contrato nomeia.
 *
 * A chama aponta para `-x` no seu próprio espaço, que é para TRÁS depois de a
 * cena a rodar com {@link missileRotation} - a mesma rotação do corpo. Nunca no
 * nariz e nunca de lado, porque não há nenhum outro ângulo por onde ela possa
 * ser desenhada.
 */
function drawFlameFrame(g: AtomicGraphics, index: number): void {
  const length = missileFlameLength(index);
  const root = FLAME_ROOT_X;
  const axis = FLAME_AXIS_Y;
  /* A fase desta chama. Muda a FORMA de quadro para quadro, e não só o tamanho. */
  const phase = (index / MISSILE_FLAME_FRAMES) * Math.PI * 2;

  /*
   * Tudo é desenhado pela borda DIREITA, e não pelo centro.
   *
   * A tubeira é o limite direito da moldura - a cena ancora o sprite em
   * `origin(1, 0.5)` - portanto uma forma centrada na raiz transborda meia
   * largura para lá dela, e isso seria fogo desenhado por CIMA do corpo do
   * míssil. Posicionar pela borda faz a aritmética dizer o que se quer: "esta
   * bolha acaba aqui, e cresce para trás".
   */
  const blob = (rightEdge: number, y: number, width: number, height: number): void => {
    g.fillEllipse(rightEdge - width / 2, y, width, height);
  };

  /*
   * O halo, primeiro e por baixo de tudo.
   *
   * Duas elipses largas e quase transparentes. São o que dá a impressão de
   * brilho sem um bloom global - que o contrato proíbe - e o que faz a chama
   * ler-se no zoom mínimo mesmo quando a pluma tem poucos pixels.
   */
  g.fillStyle(0xff5a08, 0.16);
  blob(root, axis, length * 1.05, FLAME_HALF * 3.2);
  g.fillStyle(0xffa028, 0.14);
  blob(root, axis, length * 0.62, FLAME_HALF * 2.1);

  /*
   * A pluma, em quatro camadas de bolhas sobrepostas.
   *
   * Bolhas e não triângulos: um triângulo é uma forma morta, e três triângulos
   * concêntricos são três formas mortas - foi essa a chama que a avaliação
   * manual chamou "sem graça". Uma cadeia de elipses que encolhem ao longo do
   * eixo, cada camada com a sua ondulação, sobrepõe-se a si mesma e produz a
   * variação de densidade que faz uma chama parecer fogo. O custo é o mesmo:
   * são desenhadas UMA vez, para dentro de uma textura partilhada.
   *
   * A ordem é a que o contrato nomeia e a que uma chama real se lê: vermelho na
   * extremidade, laranja no corpo, âmbar por dentro, branco-amarelo no núcleo.
   */
  const layers: readonly { colour: number; alpha: number; span: number; width: number }[] = [
    { colour: 0xff2a04, alpha: 0.3, span: 1, width: 1 },
    { colour: 0xff6a10, alpha: 0.34, span: 0.76, width: 0.82 },
    { colour: 0xffb020, alpha: 0.4, span: 0.5, width: 0.6 },
    { colour: 0xfff2c0, alpha: 0.62, span: 0.26, width: 0.36 },
  ];
  const STEPS = 9;
  for (const layer of layers) {
    g.fillStyle(layer.colour, layer.alpha);
    for (let step = 0; step < STEPS; step += 1) {
      const t = step / (STEPS - 1);
      /* Bojo perto da raiz, ponta afilada: `sin` sobe depressa e desce devagar. */
      const profile = Math.sin(Math.PI * (0.18 + 0.82 * t)) * (1 - t * 0.55);
      const half = FLAME_HALF * layer.width * profile;
      if (half <= 0.6) {
        continue;
      }
      /* A ondulação: cresce com a distância à raiz, que é onde o gás é livre. */
      const wobble = Math.sin(t * 5.2 + phase) * FLAME_HALF * 0.28 * t;
      blob(root - length * layer.span * t, axis + wobble, half * 1.9, half * 2);
    }
  }

  /*
   * Os diamantes de choque, e a razão de existirem.
   *
   * São a assinatura de um motor de foguete a queimar: nós brilhantes alinhados
   * no eixo, logo à saída da tubeira. Três pequenos, brancos e curtos, com o
   * tamanho a variar por quadro. É o detalhe que faz a chama ler-se como
   * PROPULSÃO em vez de uma mancha quente, e custa três elipses.
   */
  g.fillStyle(0xffffff, 0.55);
  for (let node = 0; node < 3; node += 1) {
    const t = 0.08 + node * 0.13;
    const size = FLAME_HALF * (0.5 - node * 0.11) * (0.85 + 0.15 * Math.sin(phase + node));
    if (size <= 0.4) {
      continue;
    }
    blob(root - length * t, axis, size * 1.5, size * 1.15);
  }

  /* E a boca da tubeira, o ponto mais quente de todos. */
  g.fillStyle(0xffffff, 0.8);
  blob(root, axis, FLAME_HALF * 1.1, FLAME_HALF * 1.5);

  /*
   * As línguas soltas, para lá da ponta.
   *
   * Duas bolhas pequenas que se desprendem, em posições diferentes a cada
   * quadro. São o que impede a animação de se ler como uma respiração regular:
   * sem elas, quadros consecutivos só diferem em comprimento.
   */
  g.fillStyle(0xff7a18, 0.34);
  for (let tongue = 0; tongue < 2; tongue += 1) {
    const step = (index + tongue * 3) % MISSILE_FLAME_FRAMES;
    const drift = 0.06 + (0.09 * step) / MISSILE_FLAME_FRAMES;
    const y = axis + Math.sin(phase * 1.7 + tongue * 2.1) * FLAME_HALF * 0.55;
    const size = FLAME_HALF * (0.34 - tongue * 0.1);
    if (size <= 0.3) {
      continue;
    }
    blob(root - length * (1 + drift), y, size * 2, size * 1.7);
  }
}

/* -------------------------------------------------------------------------- */
/*  As texturas                                                               */
/* -------------------------------------------------------------------------- */

/*
 * As chaves nomeiam o EFEITO, nunca a joia.
 *
 * O prefixo `arsenal-` distingue-o do `projectile-missile-` do pet - que é
 * outra entidade, com outra arte e outra cor - sem que nenhuma das duas chaves
 * diga que joia existe. É a mesma disciplina que faz a explosão chamar-se
 * `atomic-blast-core` e o corte chamar-se `cyclone_cut`: a arte conhece o
 * efeito, e `jewel-presentation.test.ts` falha se ela conhecer mais do que isso.
 */
const BODY_KEY = 'arsenal-missile-body';

/**
 * **O corpo do míssil: UMA textura, sem chama nenhuma dentro.**
 *
 * Eram seis, uma por quadro de propulsão. A chama saiu para o seu próprio sprite
 * - ver {@link ensureMissileFlameTexture} - e com ela saíram cinco texturas: o
 * corpo não anima, portanto não tem razão para existir seis vezes.
 *
 * O nariz está no `+x`, e é isso que faz a rotação da cena ser `atan2` e mais
 * nada.
 */
export function ensureMissileBodyTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(BODY_KEY)) {
    return BODY_KEY;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  /* As aletas primeiro, para o corpo as tapar e elas ficarem ATRÁS. */
  drawFins(g);
  drawBody(g);
  drawNose(g);
  drawNozzle(g);
  g.generateTexture(BODY_KEY, MISSILE_TEXTURE_WIDTH, MISSILE_TEXTURE_HEIGHT);
  g.destroy();
  return BODY_KEY;
}

const FLAME_KEY_PREFIX = 'arsenal-missile-flame-';

/**
 * Um quadro da propulsão, gerado uma vez e guardado no gestor de texturas.
 *
 * Seis no total para a sala inteira - **não um por míssil**. Trezentos mísseis
 * partilham estas seis imagens e um pool de sprites, que é o que o contrato pede
 * quando proíbe um emitter por míssil.
 */
export function ensureMissileFlameTexture(scene: AtomicTextureScene, frame: number): string {
  const index = ((frame % MISSILE_FLAME_FRAMES) + MISSILE_FLAME_FRAMES) % MISSILE_FLAME_FRAMES;
  const key = `${FLAME_KEY_PREFIX}${index}`;
  if (scene.textures.exists(key)) {
    return key;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  drawFlameFrame(g, index);
  g.generateTexture(key, MISSILE_FLAME_TEXTURE_WIDTH, MISSILE_FLAME_TEXTURE_HEIGHT);
  g.destroy();
  return key;
}

/* -------------------------------------------------------------------------- */
/*  A orientação, e ela é UMA (gate V28G.L.1)                                 */
/* -------------------------------------------------------------------------- */

/**
 * **A rotação de um míssil, e a única transformação que existe.**
 *
 * O `+x` local da textura é o nariz, portanto isto é `atan2` da direção
 * autoritativa e mais nada:
 *
 * | velocidade | nariz |
 * | --- | --- |
 * | `+x` | direita |
 * | `-x` | esquerda |
 * | `+y` | **baixo** no espaço Phaser |
 * | `-y` | **cima** |
 *
 * ## Porque é uma função, e não uma linha em cada cena
 *
 * Porque duas cenas com a mesma linha são duas oportunidades de a escrever com
 * uma negação diferente - foi assim que o corpo ficou de ponta-cabeça de um dos
 * lados. A caça e o duelo chamam esta, e `missile-art.test.ts` afirma as quatro
 * direções sobre ela em vez de as afirmar sobre um `atan2` copiado.
 *
 * É APRESENTAÇÃO: decide para onde um sprite aponta e nada mais. A direção que
 * ela recebe vem da autoridade, e a curva do míssil é dela e não desta função.
 */
export function missileRotation(dirX: number, dirY: number): number {
  if (!Number.isFinite(dirX) || !Number.isFinite(dirY) || (dirX === 0 && dirY === 0)) {
    /* Sem direção não há rumo. Manter o anterior é do chamador; aqui, leste. */
    return 0;
  }
  return Math.atan2(dirY, dirX);
}

/**
 * Onde a chama se agarra, como fração do COMPRIMENTO desenhado do míssil.
 *
 * Medida a partir do centro do sprite - que é onde a simulação diz que ele está
 * - até à boca da tubeira, e negativa porque a tubeira fica atrás. A cena roda
 * este deslocamento pela mesma rotação do corpo, portanto a chama nunca pode
 * aparecer no nariz nem de lado: ela está presa à traseira REAL.
 */
export const MISSILE_FLAME_ANCHOR = (TAIL_END_X - MISSILE_TEXTURE_WIDTH / 2) / MISSILE_BODY_SPAN;

/**
 * A escala da chama, relativa à do corpo.
 *
 * Maior do que um: a chama é desenhada numa moldura pequena e tem de ler-se no
 * zoom mínimo, que é a razão inteira pela qual ela saiu da textura do corpo.
 */
export const MISSILE_FLAME_SCALE = 1.35;

/**
 * **A esteira, em três números e nenhuma linha.**
 *
 * O contrato pede uma esteira "curta e fina" e diz explicitamente que ela
 * **nunca permanece durante os nove segundos inteiros**. Estes três números são
 * essa frase:
 *
 * - uma marca a cada {@link MISSILE_TRAIL_INTERVAL_MS};
 * - cada marca vive {@link MISSILE_TRAIL_FADE_MS} e desvanece linearmente;
 * - portanto no ecrã há no máximo `fade / intervalo` marcas atrás do corpo -
 *   **cinco** - qualquer que seja o tempo que o míssil já leva de voo.
 *
 * É por construção que a esteira não cresce: um nono segundo de voo tem
 * exatamente o mesmo rasto que o primeiro. E nunca é uma linha - são marcas
 * soltas, que é o que o `CLAUDE.md` exige de tudo o que sai de um cano neste
 * jogo.
 */
export const MISSILE_TRAIL_INTERVAL_MS = 44;
export const MISSILE_TRAIL_FADE_MS = 220;

/** O tamanho de uma marca, relativo à escala do corpo. Fina, por contrato. */
export const MISSILE_TRAIL_SCALE = 0.9;

const TRAIL_KEY = 'arsenal-missile-trail';

/**
 * A ESTEIRA: um pontinho quente e alongado, desenhado uma vez.
 *
 * Curta e fina por contrato, e por isso uma textura minúscula: o que faz a
 * esteira é o POOL de cópias a desvanecer, não o tamanho de cada uma. Nunca uma
 * linha contínua, nunca um traço que atravesse o mapa.
 */
export function ensureMissileTrailTexture(scene: AtomicTextureScene): string {
  if (scene.textures.exists(TRAIL_KEY)) {
    return TRAIL_KEY;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(0x8a3a12, 0.5).fillEllipse(12, 5, 24, 9);
  g.fillStyle(0xff8a3c, 0.85).fillEllipse(12, 5, 15, 5);
  g.fillStyle(0xffe6bd, 1).fillEllipse(13, 5, 7, 3);
  g.generateTexture(TRAIL_KEY, 24, 10);
  g.destroy();
  return TRAIL_KEY;
}

/* -------------------------------------------------------------------------- */
/*  A explosão                                                                */
/* -------------------------------------------------------------------------- */

/**
 * O raio VISUAL da explosão, como fração do comprimento desenhado do míssil.
 *
 * **Não é um raio mecânico.** O míssil não tem dano em área, e o evento que a
 * autoridade emite carrega raio ZERO precisamente para o dizer. Este número
 * existe para a explosão ter um tamanho, e é derivado do próprio míssil para que
 * ela continue proporcional quando ele mudar de tamanho.
 *
 * Escolhido para ser **compacta**: pouco mais de metade do comprimento do míssil
 * de raio, o que dá uma bola de fogo do tamanho dele. O contrato proíbe uma onda
 * de choque grande e proíbe um círculo que pareça área de dano, e a defesa
 * estrutural contra a segunda é não existir camada de onda nenhuma neste sistema.
 */
export const MISSILE_BLAST_RADIUS_FACTOR = 0.55;

/** O clarão inicial. Muito curto: é um instante, não um estado. */
export const MISSILE_CORE_MS = 130;
/** As chamas, que abrem para fora e apagam. */
export const MISSILE_FIRE_MS = 340;
/** As brasas e a fumaça, a camada mais longa - e ainda assim curta. */
export const MISSILE_EMBER_MS = 380;
export const MISSILE_SMOKE_MS = 460;

/** Quantas brasas uma explosão lança, no detalhe cheio. **Poucas**, por contrato. */
export const MISSILE_EMBERS = 6;

export interface MissileBlastSprite {
  x: number;
  y: number;
  rotation: number;
  alpha: number;
  setDepth(value: number): MissileBlastSprite;
  setScale(x: number, y?: number): MissileBlastSprite;
  setVisible(value: boolean): MissileBlastSprite;
  setBlendMode(value: unknown): MissileBlastSprite;
  destroy(): void;
}

export interface MissileBlastScene extends AtomicTextureScene {
  readonly tweens: {
    add(config: Record<string, unknown>): { remove(): void };
  };
  readonly add: {
    image(x: number, y: number, key: string): MissileBlastSprite;
  };
}

export interface MissileBlendModes {
  readonly add: unknown;
  readonly normal: unknown;
}

/** Uma explosão confirmada, já em unidades do mundo da cena. */
export interface MissileBlast {
  /** O id do evento. A semente da variação, e nunca um sorteio local. */
  readonly id: number;
  readonly x: number;
  readonly y: number;
  /** O raio VISUAL, nas unidades em que a cena desenha. Nunca um raio de dano. */
  readonly radius: number;
}

interface Live {
  readonly core: MissileBlastSprite;
  readonly fire: MissileBlastSprite;
  readonly smoke: MissileBlastSprite;
  readonly embers: MissileBlastSprite[];
  readonly tweens: { remove(): void }[];
  active: boolean;
}

export interface MissileBlastOptions {
  readonly depth?: number;
  readonly blend: MissileBlendModes;
  /**
   * Quanta decoração desenhar, em `[0, 1]`.
   *
   * **Só corta resíduo.** Zero apaga brasas e fumaça; o núcleo e as chamas ficam
   * sempre, porque são a explosão. É a única redução que o contrato autoriza, e
   * mesmo essa só depois de alguém medir que ela é precisa.
   */
  readonly detail?: number;
}

/**
 * **O sistema de explosões do míssil.** Três camadas, e nenhuma onda de choque.
 *
 * ## Porque não é `AtomicBlastSystem`
 *
 * Porque aquele tem cinco camadas e a terceira é a ONDA DE CHOQUE - um anel que
 * se abre até ao raio mecânico da bomba, e cuja razão de existir é representar a
 * área que fere. O míssil não tem área nenhuma, e desenhar-lhe um anel seria
 * mostrar ao jogador uma zona de dano que não existe. O contrato proíbe-o em
 * três frases distintas, e a defesa mais forte contra ele é esta: **este sistema
 * não tem camada de onda para acidentalmente ligar**.
 *
 * O que ele reutiliza são as TEXTURAS - núcleo, bola de fogo, brasa e fumaça -
 * porque são as mesmas quatro imagens e gerá-las outra vez seria pagá-las duas
 * vezes no mesmo gestor de texturas.
 *
 * ## As três camadas, e a ordem em que se leem
 *
 * 1. **núcleo branco-amarelo** - curto, colapsa em vez de crescer, diz "aqui";
 * 2. **chamas laranja e vermelhas** - a bola de fogo a abrir para fora;
 * 3. **poucas brasas e fumaça escura** - curtas, e é aqui que o detalhe corta.
 *
 * A mesma arte em impacto, parede e fim de vida: quem sabe se houve dano é a
 * autoridade, e ela di-lo com um número de dano - não com uma explosão diferente.
 *
 * Um pool, como o da Bomba: uma cadência alta com a joia encaixada produz
 * explosões seguidas, e alocar sprites por cada uma seria alocar dentro de um
 * quadro.
 */
export class MissileBlastSystem {
  private readonly pool: Live[] = [];
  private disposed = false;
  private readonly coreKey: string;
  private readonly fireKey: string;
  private readonly smokeKey: string;
  private readonly emberKey: string;

  constructor(
    private readonly scene: MissileBlastScene,
    private readonly options: MissileBlastOptions,
  ) {
    this.coreKey = ensureAtomicCoreTexture(scene);
    this.fireKey = ensureAtomicFireballTexture(scene);
    this.smokeKey = ensureAtomicSmokeTexture(scene);
    this.emberKey = ensureAtomicEmberTexture(scene);
  }

  private get depth(): number {
    return this.options.depth ?? 900;
  }

  private get detail(): number {
    const value = this.options.detail ?? 1;
    return value < 0 ? 0 : value > 1 ? 1 : value;
  }

  spawn(blast: MissileBlast): void {
    if (this.disposed || blast.radius <= 0) {
      return;
    }
    const live = this.take();
    live.active = true;
    const { x, y, radius } = blast;

    /* 1. O NÚCLEO: nasce grande e COLAPSA, para o meio ler-se como a fonte. */
    this.place(live.core, x, y, this.depth + 2, this.options.blend.add);
    live.core.setScale((radius * 1.15) / 64);
    const bare = this.detail <= 0;
    this.push(live, {
      targets: live.core,
      scaleX: (radius * 0.3) / 64,
      scaleY: (radius * 0.3) / 64,
      alpha: 0,
      duration: MISSILE_CORE_MS,
      ease: 'Quad.easeOut',
    });

    /* 2. AS CHAMAS: abrem para fora e apagam. Sem anel, sem frente, sem onda. */
    this.place(live.fire, x, y, this.depth + 1, this.options.blend.add);
    live.fire.setScale((radius * 0.34) / 80);
    /* Um ângulo por explosão, do ID do evento: seis clientes veem a mesma. */
    live.fire.rotation = (blast.id % 12) * 0.5236;
    this.push(live, {
      targets: live.fire,
      scaleX: (radius * 1.6) / 80,
      scaleY: (radius * 1.42) / 80,
      duration: MISSILE_FIRE_MS,
      ease: 'Cubic.easeOut',
    });
    this.push(live, {
      targets: live.fire,
      alpha: 0,
      duration: MISSILE_FIRE_MS,
      ease: 'Quad.easeIn',
      /*
       * No detalhe zero as chamas são a última camada viva, portanto são elas que
       * largam o slot. Um segundo tween sobre o mesmo alpha só para o largar
       * seriam dois tweens a escrever no mesmo campo.
       */
      ...(bare ? { onComplete: () => this.release(live) } : {}),
    });

    if (bare) {
      return;
    }

    /* 3a. AS BRASAS, lançadas do centro. Poucas, e curtas. */
    const count = Math.max(0, Math.round(MISSILE_EMBERS * this.detail));
    for (let index = 0; index < live.embers.length; index += 1) {
      const ember = live.embers[index] as MissileBlastSprite;
      if (index >= count) {
        ember.setVisible(false);
        continue;
      }
      /*
       * A direção vem do ID DO EVENTO e do índice - nunca de um sorteio local -
       * portanto seis clientes veem a mesma explosão. É a mesma regra que o
       * Ciclone e a Bomba já seguem.
       */
      const angle =
        ((index + (blast.id % 5) * 0.191) / Math.max(1, count)) * Math.PI * 2 +
        (blast.id % 11) * 0.13;
      const reach = radius * (0.7 + ((blast.id + index) % 4) * 0.09);
      this.place(ember, x, y, this.depth + 1, this.options.blend.add);
      ember.rotation = angle;
      ember.setScale(0.6 + radius / 220);
      this.push(live, {
        targets: ember,
        x: x + Math.cos(angle) * reach,
        y: y + Math.sin(angle) * reach,
        alpha: 0,
        duration: MISSILE_EMBER_MS,
        ease: 'Cubic.easeOut',
      });
    }

    /* 3b. A FUMAÇA escura, em `NORMAL`: a única camada que escurece. */
    this.place(live.smoke, x, y, this.depth, this.options.blend.normal);
    live.smoke.alpha = 0.7 * this.detail;
    live.smoke.setScale((radius * 0.45) / 48);
    this.push(live, {
      targets: live.smoke,
      scaleX: (radius * 1.15) / 48,
      scaleY: (radius * 1.15) / 48,
      alpha: 0,
      duration: MISSILE_SMOKE_MS,
      ease: 'Sine.easeOut',
      onComplete: () => this.release(live),
    });
  }

  /** Quantas explosões estão vivas agora. Para as provas e para as medições. */
  liveCount(): number {
    return this.pool.filter((entry) => entry.active).length;
  }

  /** Quantos conjuntos o sistema alocou. Nunca deve crescer sem uso. */
  pooledCount(): number {
    return this.pool.length;
  }

  /** Apaga o que está no ecrã sem largar os objetos. Para uma correção. */
  clear(): void {
    for (const live of this.pool) {
      this.release(live);
    }
  }

  /**
   * Larga tudo. Os tweens saem ANTES dos sprites: um tween cujo alvo foi
   * destruído continua a correr e a escrever num objeto morto.
   */
  destroy(): void {
    this.disposed = true;
    for (const live of this.pool) {
      for (const tween of live.tweens) {
        tween.remove();
      }
      live.tweens.length = 0;
      live.core.destroy();
      live.fire.destroy();
      live.smoke.destroy();
      for (const ember of live.embers) {
        ember.destroy();
      }
      live.embers.length = 0;
    }
    this.pool.length = 0;
  }

  /* ---------------------------------------------------------------- pool -- */

  private take(): Live {
    const free = this.pool.find((entry) => !entry.active);
    if (free) {
      return free;
    }
    const live: Live = {
      core: this.spawnSprite(this.coreKey),
      fire: this.spawnSprite(this.fireKey),
      smoke: this.spawnSprite(this.smokeKey),
      embers: Array.from({ length: MISSILE_EMBERS }, () => this.spawnSprite(this.emberKey)),
      tweens: [],
      active: false,
    };
    this.pool.push(live);
    return live;
  }

  private spawnSprite(key: string): MissileBlastSprite {
    const sprite = this.scene.add.image(0, 0, key);
    sprite.setVisible(false).setDepth(this.depth);
    return sprite;
  }

  private place(
    sprite: MissileBlastSprite,
    x: number,
    y: number,
    depth: number,
    blend: unknown,
  ): void {
    sprite.x = x;
    sprite.y = y;
    sprite.alpha = 1;
    sprite.rotation = 0;
    sprite.setDepth(depth).setBlendMode(blend).setVisible(true);
  }

  private push(live: Live, config: Record<string, unknown>): void {
    live.tweens.push(this.scene.tweens.add(config));
  }

  private release(live: Live): void {
    for (const tween of live.tweens) {
      tween.remove();
    }
    live.tweens.length = 0;
    live.active = false;
    for (const sprite of [live.core, live.fire, live.smoke, ...live.embers]) {
      sprite.setVisible(false);
      sprite.alpha = 0;
    }
  }
}
